import { Box, Container, Stack, Typography } from "@mui/material";
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { tokens } from "../theme";
import { usePageChrome } from "../context/PageChrome";
import HelpBadge from "./HelpBadge";

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Small uppercase brand kicker shown above the title (with a brand dot). */
  eyebrow?: string;
  /** Optional explanation shown behind a "?" badge next to the title. */
  help?: ReactNode;
  /** Right-aligned actions (buttons, filter dropdowns). */
  actions?: ReactNode;
}

/**
 * The page's identity: an optional uppercase eyebrow, a Montserrat display
 * title, an optional subtitle and right-aligned actions.
 *
 * Inside the CMS shell it renders into the sticky app header, so the page
 * title and the global scope selectors share one row. Rendered outside the
 * shell (a standalone surface, a test) it falls back to an inline panel, so
 * the component is still self-sufficient.
 */
export function PageHeader({
  title,
  subtitle,
  eyebrow,
  help,
  actions,
}: PageHeaderProps) {
  const chrome = usePageChrome();
  const claim = chrome?.claim;

  useEffect(() => {
    if (!claim) return;
    claim(1);
    return () => claim(-1);
  }, [claim]);

  const heading = (
    <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
      {eyebrow && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 0.25 }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: tokens.brand,
            }}
          />
          <Typography
            variant="micro"
            sx={{
              fontFamily: '"Inter Tight", Inter, sans-serif',
              typography: "eyebrow",
              color: tokens.brand,
            }}
          >
            {eyebrow}
          </Typography>
        </Stack>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          component="h1"
          sx={{
            typography: "brand",
            fontSize: { xs: "1.35rem", md: "1.5rem" },
            lineHeight: 1.15,
            color: "text.primary",
          }}
        >
          {title}
        </Typography>
        {help && <HelpBadge title={help} corner={false} placement="top" />}
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  const actionBox = actions ? (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      {actions}
    </Box>
  ) : null;

  // In-shell: one row inside the sticky header, next to the scope selectors.
  if (chrome?.slot) {
    return createPortal(
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {heading}
        {actionBox}
      </Box>,
      chrome.slot
    );
  }

  // Standalone fallback: the soft brand-tinted panel with a left accent bar.
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        px: { xs: 2.25, md: 3 },
        py: { xs: 1.75, md: 2 },
        background: `linear-gradient(120deg, ${tokens.brandTint} 0%, ${tokens.brandTintSoft} 32%, transparent 72%)`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: `linear-gradient(180deg, ${tokens.brand}, ${tokens.brandStrong})`,
        }}
      />
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: { md: "flex-end" },
        }}
      >
        {heading}
        {actionBox}
      </Stack>
    </Box>
  );
}

interface PageContainerProps {
  children: ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | false;
}

/** Standard page canvas: full-width container with consistent vertical rhythm. */
export function PageContainer({
  children,
  maxWidth = false,
}: PageContainerProps) {
  return (
    <Container maxWidth={maxWidth} sx={{ py: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>{children}</Stack>
    </Container>
  );
}

export default PageHeader;
